import { LegacyCard, Spinner, Text, Button, Badge, Tooltip } from "@shopify/polaris";
import React, { useEffect, useMemo } from "react";
import { usePolarisTypedTable } from "@vendor-app/app/_sharedComponents/TypedTable/usePolarisTypedTable";
import { UploadFileMetadataOutput } from "../../libs/types";
import { MigrationsTableAccess } from "@vendor-app/app/_sharedComponents/FileImports/stores/MigrationsTable";
import { useToast } from "@vendor-app/app/_sharedComponents/Toast/ToastProvider";
import {
  badgeStatusMap,
  blockedStatuses,
  uploadsTableColumns,
  statusTextMap,
} from "@vendor-app/app/_sharedComponents/FileImports/libs/utils/constants";
import { SpinnerContainer, TableScrollContainer } from "@vendor-app/app/_sharedComponents/FileImports/styles";

export const MigrationUploadsTable = () => {
  const { addToast } = useToast();
  const actions = MigrationsTableAccess.useActions();
  const files = MigrationsTableAccess.useFiles();
  const isLoading = MigrationsTableAccess.useLoading();
  const error = MigrationsTableAccess.useError();
  const { Table } = usePolarisTypedTable();

  const isUploadingOrCommitting = useMemo(() => {
    return files.some(file => blockedStatuses.includes(file.status));
  }, [files]);

  const renderDownloadAction = (file: UploadFileMetadataOutput) => {
    switch (file.status) {
      case "failed_pre_processing": {
        return (
          <Button onClick={() => actions.downloadFeedback(file.id, "pre-commit")}>Pre-commit Feedback</Button>
        );
      }

      case "successful_pre_processing": {
        return (
          <Button primary disabled={isUploadingOrCommitting} onClick={() => actions.commitFileById(file.id)}>
            Commit
          </Button>
        );
      }

      case "failed_processing":
      case "partially_successful_processing":
      case "successful_processing": {
        return <Button onClick={() => actions.downloadFeedback(file.id, "commit")}>Commit Feedback</Button>;
      }

      default: {
        return (
          <Text as="span" color="subdued">
            No files available
          </Text>
        );
      }
    }
  };

  const renderUploaded = (file: UploadFileMetadataOutput) => {
    if (file.uploadedCsvFileBucketPath) {
      return <Button onClick={() => actions.downloadUploaded(file.id, file.fileName)}>Uploaded File</Button>;
    }

    return (
      <Text as="span" color="subdued">
        Upload in progress
      </Text>
    );
  };

  useEffect(() => {
    actions.fetchFiles();
  }, []);

  useEffect(() => {
    if (error) {
      addToast(`Failed to perform action: ${error}`, true);
      actions.clearError();
    }
  }, [error]);

  const dataWithActionAndStatus = files.map((file: UploadFileMetadataOutput) => ({
    ...file,
    fileName: (
      <Tooltip
        width="wide"
        content={
          <div>
            {file.fileName}
            <div>
              Uploaded by: <b>{file.uploadedBy}</b>
            </div>
          </div>
        }
      >
        <div style={{ maxWidth: "250px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {file.fileName}
        </div>
      </Tooltip>
    ),
    status: (
      <Badge status={badgeStatusMap[file.status] ?? "new"}>
        {statusTextMap[file.status] || file.status.replaceAll("_", " ")}
      </Badge>
    ),
    actions: renderDownloadAction(file),
    uploadedFiles: renderUploaded(file),
  }));

  const header = (
    <LegacyCard.Section>
      <Text as="h3" variant="headingLg">
        Past imports
      </Text>
    </LegacyCard.Section>
  );

  if (isLoading) {
    return (
      <LegacyCard>
        {header}
        <LegacyCard.Section>
          <SpinnerContainer>
            <Spinner accessibilityLabel="Loading files" size="large" />
          </SpinnerContainer>
        </LegacyCard.Section>
      </LegacyCard>
    );
  }

  return (
    <LegacyCard>
      <LegacyCard>
        {header}
        <TableScrollContainer>
          <Table
            selectable={false}
            columns={uploadsTableColumns as any}
            data={dataWithActionAndStatus}
            itemCount={files.length}
            resourceName={{ singular: "import", plural: "imports" }}
            emptyStateText="No past imports"
          />
        </TableScrollContainer>
      </LegacyCard>
    </LegacyCard>
  );
};
